import { Action, configureStore, ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { DEV } from 'application/consts';

import { apolloClient, setAppDispatch } from 'application/providers/DataProvider/DataProvider';

import { createAcceptanceAdvancedListener } from './slices/acceptanceAdvancedSlice/acceptanceAdvancedListener';
import { createAppInitListener, setPersistor } from './slices/applicationSlice/appInitListener';
import { createGlobalPollingListener } from './slices/globalPollingSlice/globalPollingListener';
import { createRealtimeListener } from './slices/realtimeSlice/realtimeListener';

import rootReducer from './slices';

export interface ThunkExtra {
  apolloClient: ApolloClient<NormalizedCacheObject>;
}

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['account'],
  version: 1,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const extra: ThunkExtra = { apolloClient };

const acceptanceAdvancedListener = createAcceptanceAdvancedListener(apolloClient);
const appInitListener = createAppInitListener(apolloClient);
const globalPollingListener = createGlobalPollingListener(apolloClient);
const realtimeListener = createRealtimeListener(apolloClient);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'persist/PERSIST',
          'acceptanceAdvancedSigningProcess/startAcceptanceAdvancedSigning',
          'globalPolling/startGlobalPolling',
          'account/fetchData/pending',
          'account/fetchData/fulfilled',
          'account/fetchData/rejected',
        ],
      },
      thunk: {
        extraArgument: extra,
      },
    }).prepend([
      appInitListener.middleware,
      globalPollingListener.middleware,
      acceptanceAdvancedListener.middleware,
      realtimeListener.middleware,
    ]),
  devTools: DEV,
});

export const persistor = persistStore(store);
setPersistor(persistor);
setAppDispatch(store.dispatch);

export type AppStore = typeof store;
export type AppPersistor = typeof persistor;
export type AppDispatch = ThunkDispatch<RootState, ThunkExtra, UnknownAction>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  { apolloClient: ApolloClient<NormalizedCacheObject> },
  Action<string>
>;

export * from './slices';
